import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Loader2, Copy, Check, ExternalLink, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function RetailProposalPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data } = await supabase.from("digital_proposals").select("*").eq("id", id).maybeSingle();
      setProposal(data);
      setLoading(false);
    })();
  }, [id]);

  const shareUrl = proposal ? `${window.location.origin}/digital-proposal/${proposal.share_id}` : "";

  const copy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Share link copied!");
    setTimeout(() => setCopied(false), 1500);
  };

  const markSent = async () => {
    if (!proposal) return;
    await supabase.from("digital_proposals").update({ status: "sent", sent_at: new Date().toISOString() }).eq("id", proposal.id);
    setProposal({ ...proposal, status: "sent" });
    toast.success("Marked as sent");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Proposal not found</p>
          <Button variant="outline" onClick={() => navigate("/retail")}>New Quick Quote</Button>
        </div>
      </div>
    );
  }

  const tiers = (proposal.tiers as any[]) || [];
  const selected = tiers.find((t) => t.key === proposal.selected_tier) || tiers[1] || tiers[0];

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-10 bg-card border-b border-border/30 px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/retail")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-bold text-foreground">Proposal Ready</h1>
          <p className="text-xs text-muted-foreground truncate">{proposal.property_address}</p>
        </div>
        <Badge variant="secondary" className="text-[10px]">{proposal.status}</Badge>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        <div className="rounded-xl border border-border/40 bg-card p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Prepared For</p>
          <p className="text-sm font-bold">{proposal.customer_name}</p>
          <p className="text-xs text-muted-foreground">{proposal.property_address}</p>
        </div>

        {selected && (
          <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-5 text-center">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Selected Package</p>
            <p className="text-3xl font-extrabold">${Number(selected.price).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">{selected.product_line}</p>
          </div>
        )}

        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <ExternalLink className="h-4 w-4 text-primary" />
            <p className="text-xs font-semibold uppercase tracking-wider">Customer Signing Link</p>
          </div>
          <div className="rounded-lg bg-muted/50 p-3">
            <p className="text-xs break-all font-mono text-muted-foreground">{shareUrl}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={copy}>
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              Copy Link
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => window.open(shareUrl, "_blank")}>
              <ExternalLink className="h-3.5 w-3.5" /> Preview
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          {tiers.map((t: any) => (
            <div key={t.key} className={`rounded-lg border p-3 ${t.key === proposal.selected_tier ? "border-primary bg-primary/5" : "border-border/40"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase text-muted-foreground">{t.name}</p>
                  <p className="text-sm font-semibold">{t.product_line}</p>
                </div>
                <p className="text-base font-bold">${Number(t.price).toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>

        {proposal.status === "draft" && (
          <Button className="h-12 w-full gap-2 font-bold" onClick={markSent}>
            <Send className="h-4 w-4" /> Mark as Sent
          </Button>
        )}
      </div>
    </div>
  );
}
